import { BaseClient } from './baseClient';

/**
 * In-app feedback. One endpoint: the signed-in person tells us what they
 * think, and the server attributes it from their token — the browser never
 * names an author, an organization or a status.
 *
 * Screenshots are deliberately NOT part of this contract: capturing one needs
 * a canvas-rasteriser dependency this app does not carry, and adding a heavy
 * one for a nice-to-have is the wrong trade. The attached context below is
 * what we send instead, and the panel shows the person exactly that list.
 */

export type FeedbackKind = 'PROBLEM' | 'IDEA' | 'PRAISE';

/** Facts the browser attaches automatically — all shown before sending. */
export interface FeedbackContext {
    /** In-app route, never the full URL: a query string could carry anything. */
    path: string;
    /** Human RFQ id when the page had one. */
    rfqId?: string;
    userAgent: string;
    /** "1440x900", as the browser reports it. */
    viewport: string;
}

export interface FeedbackSubmission {
    kind: FeedbackKind;
    message: string;
    context: FeedbackContext;
}

export interface FeedbackReceipt {
    id: string;
    kind: FeedbackKind;
    createdAt: string;
}

interface Envelope<T> {
    success: boolean;
    data: T;
}

class FeedbackClient extends BaseClient {
    /** 201 with the receipt; a 400 carries a coded, readable refusal. */
    async submit(submission: FeedbackSubmission): Promise<FeedbackReceipt> {
        const response = await this.api.post<Envelope<FeedbackReceipt>>('/trp/feedback', submission);
        return response.data.data;
    }
}

export const feedbackClient = new FeedbackClient();
